import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { gsap } from 'gsap';
import lottie from 'lottie-web';

export class GalleryScene {
    constructor(app) {
        this.app = app;
        this.scene = new THREE.Scene();

        this.projectModels = [];
        this.pedestals = [];
        this.currentProject = 0;
        this.totalProjects = 5;

        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        this.isZoomedIn = false;
        this.zoomedPedestal = null;
        this.isScrolling = false;
        this.isZoomAnimating = false;
        this.isHoveringBooth = false;
        this.isCharacterAllowedToShow = true; // Initialize - character visible in overview mode
        this.lastZoomClickTime = 0; // Track last click time to prevent rapid clicking

        this.hasSwipedOnce = false; // Track if user has swiped/scrolled when zoomed in
        this.hasShownInitialChatbox = false; // Track if chatbox has been shown on first entry

        this.setupScene();
    }

    async init() {
        this.createGallerySpace();
        this.createBooth();
        this.createCharacter();
        this.createPedestals();
        this.createProjectDisplays();
        this.setupLighting();
        this.setupInteractions();
        this.createBackButton();

        this.app.sceneManager.addScene('gallery', this.scene);

        // All pedestals are already visible, no need to show just one

        console.log('Gallery scene initialized with', this.totalProjects, 'projects');
        console.log('First pedestal visible:', this.pedestals[0].visible);
    }

    setupScene() {
        // Use transparent background to show the CSS background
        this.scene.background = null;
        // Remove fog to improve visibility
        // this.scene.fog = new THREE.Fog(0x000000, 15, 50);
    }

    createGallerySpace() {
        // Dark hardwood floor for vintage museum feel
        const floorGeometry = new THREE.PlaneGeometry(80, 50);
        const floorMaterial = new THREE.MeshLambertMaterial({
            color: 0x3D2817, // Rich dark brown wood
            transparent: false
        });

        const floor = new THREE.Mesh(floorGeometry, floorMaterial);
        floor.rotation.x = -Math.PI / 2;
        floor.position.y = -2;
        floor.receiveShadow = true;
        this.scene.add(floor);

        // Warm cream ceiling for classic museum atmosphere
        const ceilingGeometry = new THREE.PlaneGeometry(80, 50);
        const ceilingMaterial = new THREE.MeshLambertMaterial({ color: 0xEDE7DC });
        const ceiling = new THREE.Mesh(ceilingGeometry, ceilingMaterial);
        ceiling.rotation.x = Math.PI / 2;
        ceiling.position.y = 8;
        this.scene.add(ceiling);

        this.createWalls();
    }

    createWalls() {
        // Warm beige walls for vintage gallery aesthetic
        const wallMaterial = new THREE.MeshLambertMaterial({ color: 0xEDE7DC });
        
        const backWallGeometry = new THREE.PlaneGeometry(80, 10);
        const backWall = new THREE.Mesh(backWallGeometry, wallMaterial);
        backWall.position.set(0, 3, -25);
        this.scene.add(backWall);
        
        const sideWallGeometry = new THREE.PlaneGeometry(50, 10);
        const leftWall = new THREE.Mesh(sideWallGeometry, wallMaterial);
        leftWall.rotation.y = Math.PI / 2;
        leftWall.position.set(-40, 3, 0);
        this.scene.add(leftWall);
        
        const rightWall = new THREE.Mesh(sideWallGeometry, wallMaterial);
        rightWall.rotation.y = -Math.PI / 2;
        rightWall.position.set(40, 3, 0);
        this.scene.add(rightWall);
        
        // Add front walls to prevent seeing outside when sliding
        const frontWallGeometry = new THREE.PlaneGeometry(80, 10);
        const frontWall = new THREE.Mesh(frontWallGeometry, wallMaterial);
        frontWall.position.set(0, 3, 25);
        frontWall.rotation.y = Math.PI;
        this.scene.add(frontWall);
    }

    createBooth() {
        if (!this.app.assets.booth) {
            console.error('Booth model not loaded');
            return;
        }

        // Clone the loaded booth model
        this.booth = this.app.assets.booth.scene.clone();

        // Position booth beside the character at same depth
        // Character is at (-26, 1.5, -2)
        // Place booth at x=-22, z=-2 to be beside character at same depth
        // Camera at (0, 5, 12) with 90° FOV
        this.booth.position.set(-10, 0, 4);

        // Flip booth 180 degrees to face away from camera (toward character)
        // Original angle was toward camera, now add π to flip it around
        this.booth.rotation.y = Math.PI - Math.atan2(22, 14) + Math.PI;  // Flip 180 degrees
        this.booth.rotation.x = 0;  // Remove upward tilt since booth faces character, not camera

        // Scale adjustment (modify as needed based on booth.glb dimensions)
        this.booth.scale.setScalar(1);

        // Disable shadows
        this.booth.traverse((child) => {
            if (child.isMesh) {
                child.castShadow = false;
                child.receiveShadow = false;
            }
        });

        this.scene.add(this.booth);

        // Hide booth on mobile and tablet devices (only visible on desktop)
        if (this.app.deviceDetector.isMobileSize() || this.app.deviceDetector.isTabletSize() || this.app.deviceDetector.isMobile) {
            this.booth.visible = false;
            console.log('Booth hidden on mobile/tablet device');
        }

        // Create invisible clickbox that encompasses both booth and character
        // Character at (-13, 4, 2) above and to the left, booth at (-10, 0, 4)
        // Center: x = (-13 + -10)/2 = -11.5, y = (4 + 0)/2 = 2, z = (2 + 4)/2 = 3
        // Tight fit: width=3.5 (x span of 3 + margin), height=4.5 (y span of 4 + margin), depth=2.5 (z span of 2 + margin)
        const clickboxGeometry = new THREE.BoxGeometry(3.5, 4.5, 2.5);
        const clickboxMaterial = new THREE.MeshBasicMaterial({
            transparent: true,
            opacity: 0,
            side: THREE.DoubleSide
        });
        this.boothClickbox = new THREE.Mesh(clickboxGeometry, clickboxMaterial);

        // Position clickbox to cover character at (-13, 4, 2) and booth at (-10, 0, 4)
        // Center of clickbox: x = -11.5, y = 2, z = 3
        this.boothClickbox.position.set(-11.5, 2, 3);
        this.boothClickbox.name = 'boothClickbox';

        this.scene.add(this.boothClickbox);
    }

    createCharacter() {
        console.log('Creating Lottie character for gallery...');

        // Create 3D marker for character positioning behind the booth
        this.characterMarker = new THREE.Object3D();
        this.characterMarker.position.set(-13, 4, 2);  // Behind booth at (-10, 0, 4)
        this.scene.add(this.characterMarker);
        console.log('Character 3D marker created at:', this.characterMarker.position);

        // Create a container div for the Lottie animation
        this.characterContainer = document.createElement('div');
        this.characterContainer.id = 'gallery-lottie-character';
        this.characterContainer.style.cssText = `
            position: fixed;
            width: 200px;
            height: 200px;
            z-index: 15;
            opacity: 1;
            pointer-events: none;
            transform: scaleX(-1) rotate(10deg);
            cursor: pointer;
            transition: transform 0.3s ease, filter 0.3s ease;
            visibility: visible;
        `;

        document.body.appendChild(this.characterContainer);
        console.log('Gallery character container added to DOM');

        // Initially hide the character (will be shown when gallery scene is activated)
        this.characterContainer.style.opacity = '0';

        // Load the Lottie animation
        try {
            this.lottieAnimation = lottie.loadAnimation({
                container: this.characterContainer,
                renderer: 'svg',
                loop: false,
                autoplay: false,
                path: './animated-guy.json'
            });

            console.log('Lottie animation loading for gallery...', this.lottieAnimation);

            // Add event handling for Lottie loading
            this.lottieAnimation.addEventListener('DOMLoaded', () => {
                console.log('Gallery Lottie character loaded successfully');

                // Get total frames and calculate halfway point
                this.totalFrames = this.lottieAnimation.totalFrames;
                this.halfFrames = Math.floor(this.totalFrames / 2);
                console.log(`Animation loaded: ${this.totalFrames} total frames, locking at frame ${this.halfFrames}`);

                // Lock at halfway frame where character looks right
                this.lottieAnimation.goToAndStop(this.halfFrames, true);
            });

            this.lottieAnimation.addEventListener('error', (error) => {
                console.error('Error loading gallery Lottie character:', error);
            });

        } catch (error) {
            console.error('Failed to create gallery Lottie animation:', error);
        }

        // Add click event listener to character
        this.characterContainer.addEventListener('click', this.onCharacterClick.bind(this));

        // Add hover effect (preserve horizontal flip and rotation)
        this.characterContainer.addEventListener('mouseenter', () => {
            this.characterContainer.style.transform = 'translate(-50%, -50%) scaleX(-1.1) scaleY(1.1) rotate(10deg)';
            this.characterContainer.style.filter = 'brightness(1.2) drop-shadow(0 0 10px rgba(255, 255, 255, 0.5))';
        });

        this.characterContainer.addEventListener('mouseleave', () => {
            this.characterContainer.style.transform = 'translate(-50%, -50%) scaleX(-1) scaleY(1) rotate(10deg)';
            this.characterContainer.style.filter = 'none';
        });

        this.characterVisible = true;
    }

    onCharacterClick() {
        // Safety check: only handle clicks when in gallery mode
        if (!this.app.isInGallery()) {
            console.log('Character click ignored - not in gallery mode');
            return;
        }

        console.log('Character clicked - opening chatbox');
        this.showChatbox();
    }

    createBackButton() {
        // Create back button that appears when zoomed in
        this.backButton = document.createElement('button');

        // Set initial styling
        this.updateBackButtonStyle();

        this.backButton.addEventListener('click', () => {
            this.zoomOut();
        });

        this.backButton.addEventListener('mouseenter', () => {
            this.backButton.style.background = 'rgba(255, 255, 255, 0.2)';
        });

        this.backButton.addEventListener('mouseleave', () => {
            this.backButton.style.background = 'rgba(0, 0, 0, 0.8)';
        });

        document.body.appendChild(this.backButton);
    }

    updateBackButtonStyle() {
        if (!this.backButton) return;

        // Check if mobile to adjust positioning
        const isMobile = this.app.deviceDetector.isMobileSize() || this.app.deviceDetector.isMobile;

        // Use shorter text and icon-style on mobile
        this.backButton.innerHTML = isMobile ? '←' : '← Back to Gallery';

        // Store current opacity to preserve it during update
        const currentOpacity = this.backButton.style.opacity;
        const currentPointerEvents = this.backButton.style.pointerEvents;

        this.backButton.style.cssText = `
            position: fixed;
            ${isMobile ? 'bottom: 1.2rem; left: 1.2rem;' : 'top: 20px; left: 20px;'}
            padding: ${isMobile ? '6px 10px' : '12px 24px'};
            background: rgba(0, 0, 0, 0.8);
            color: white;
            border: 2px solid white;
            border-radius: ${isMobile ? '6px' : '8px'};
            cursor: pointer;
            z-index: 25;
            opacity: ${currentOpacity || '0'};
            pointer-events: ${currentPointerEvents || 'none'};
            transition: opacity 0.3s ease, background 0.2s ease;
            font-family: 'Inter', sans-serif;
            font-size: ${isMobile ? '1.2rem' : '0.95rem'};
            font-weight: 600;
            line-height: 1;
        `;
    }

    createPedestals() {
        // Get responsive scale factor
        const scaleFactor = this.app.deviceDetector.getResponsiveScaleFactor();

        for (let i = 0; i < this.totalProjects; i++) {
            const pedestalGroup = new THREE.Group();

            const baseGeometry = new THREE.CylinderGeometry(1.5, 1.8, 0.3, 8);
            const baseMaterial = new THREE.MeshLambertMaterial({ color: 0xaaaaaa });
            const base = new THREE.Mesh(baseGeometry, baseMaterial);
            base.position.y = -1.85;
            base.castShadow = true;

            const columnGeometry = new THREE.CylinderGeometry(1.2, 1.2, 2, 8);
            const columnMaterial = new THREE.MeshLambertMaterial({ color: 0xcccccc });
            const column = new THREE.Mesh(columnGeometry, columnMaterial);
            column.position.y = -0.7;
            column.castShadow = true;

            const topGeometry = new THREE.CylinderGeometry(1.5, 1.4, 0.2, 8);
            const topMaterial = new THREE.MeshLambertMaterial({ color: 0xdddddd });
            const top = new THREE.Mesh(topGeometry, topMaterial);
            top.position.y = 0.4;
            top.castShadow = true;

            pedestalGroup.add(base, column, top);

            const xPosition = (i - 2) * 6;
            const yPosition = this.app.deviceDetector.isMobileSize() ? -1.2 : 0;
            pedestalGroup.position.set(xPosition, yPosition, -5);

            // Apply responsive scaling
            pedestalGroup.scale.set(scaleFactor, scaleFactor, scaleFactor);

            // Show all pedestals initially
            pedestalGroup.visible = true;

            this.pedestals.push(pedestalGroup);
            this.scene.add(pedestalGroup);
        }
    }

    createProjectDisplays() {
        const projectData = [
            {
                color: 0x7B68EE,
                shape: 'dodecahedron',
                size: 0.7,
                title: "Robotics Startup - Full Stack",
                problem: "Early-stage robotics startup needed to establish brand presence and demonstrate AI capabilities to investors and customers while preparing for funding round.",
                solution: "Led end-to-end design and development of company website with custom branding, establishing strong visual identity and online presence.",
                role: "Designer & Full-Stack Developer - Led all design decisions, user research, branding, and technical implementation. Collaborated with CEO on product strategy.",
                outcomes: "• Launched website for upcoming funding round\n• Designed brand identity adopted across all company materials",
                technologies: "UI/UX Design, Web Development, Branding",
                year: "2025",
                category: "Product Design & AI",
                logo: "LOGO-IA.png",
                mockupImage: "IA-website-page.png"
            },
            {
                color: 0x4ecdc4,
                shape: 'torus',
                size: [0.6, 0.3, 16, 100],
                title: "Multi-Agent AI Chat Platform",
                problem: "Users needed to interact with specialized agentic chatbots, each optimized for different tasks, but existing interfaces made it difficult to manage multiple specialized agents simultaneously.",
                solution: "Designed and built intuitive multi-tab chat interface with persistent conversation state. Implemented real-time streaming API with optimistic UI updates, deployed scalable Docker infrastructure on Google Cloud with MongoDB for conversation persistence.",
                role: "Full-Stack Developer - Conducted user research with 8 beta testers, designed complete UI/UX, built frontend with Svelte/TypeScript and backend infrastructure. Collaborated with team on API design.",
                outcomes: "• Reduced context-switching time by 40% vs. single-chat interface\n• Achieved <100ms message render latency with streaming\n• Successfully handles 50+ concurrent agent conversations",
                technologies: "UI/UX, Svelte, TypeScript, MongoDB, Docker",
                year: "2024-2025",
                category: "Full-Stack Development",
                logo: "economy-v1-picture.png",
                mockupImage: "economy-v1-agents.jpeg"
            },
            {
                color: 0xED1C24,
                shape: 'box',
                size: [1, 1, 1],
                title: "Data Visualization Dashboard - AMD",
                problem: "AMD's chip validation team manually analyzed failure data across spreadsheets, making it difficult to identify patterns and prioritize critical issues. Testing cycles took too long, delaying product releases.",
                solution: "Designed interactive dashboard consolidating failure analysis data with intuitive visualizations (charts, heatmaps, filters). Automated chip validation workflows with Python scripts in Unix environment, streamlining repetitive testing tasks.",
                role: "Product Development Intern - Conducted user research with validation engineers, designed complete dashboard UI/UX in Dataiku, implemented Python automation scripts. Collaborated with engineering team on requirements.",
                outcomes: "• Reduced testing time by 30% through automation\n• Dashboard adopted by entire validation team\n• Code used in production to validate hundreds of thousands of units daily\n• Presented solution to senior leadership",
                technologies: "Dashboard Design, Python, Unix, Dataiku",
                year: "2024",
                category: "Data Viz & Automation",
                logo: "amdlogo.png"
            },
            {
                color: 0xFBBC04,
                shape: 'sphere',
                size: 0.6,
                title: "Google Hackathon - 1st Place",
                problem: "Buyers on 2nd-hand thrift marketplaces lack detailed insight into clothing quality and condition, making it difficult to make informed purchasing decisions. Sellers struggle to accurately represent their items, leading to disputes and returns.",
                solution: "Designed web application marketplace for thrifted clothing with AI-powered quality analysis. Built computer vision model on Google Cloud AI Platform using 740 labeled images to help buyers assess clothing condition and help sellers create accurate, detailed product listings.",
                role: "Group Leader & ML Engineer - Led team and project direction, co-designed marketplace UI/UX mockups, trained ML model for clothing quality detection. Won 1st place among 50+ teams.",
                outcomes: "• Achieved 92% accuracy in clothing condition assessment\n• Enabled buyers to make more informed purchasing decisions\n• Protected sellers with objective quality validation\n• Won 1st place at Google Cloud Hackathon",
                technologies: "UI Design, Google Cloud AI, Vertex AI, JavaScript",
                year: "2024",
                category: "AI/ML & Design",
                logo: "googlelogo.png",
                mockupImage: "Gooders_Mockup.png",
                demoVideo: "Gooders_demo.m4v"
            },
            {
                color: 0x45b7d1,
                shape: 'cone',
                size: [0.6, 1.2, 8],
                title: "Interactive 3D Portfolio",
                description: "Built this immersive 3D portfolio for fun as a creative way to showcase my work. Designed as a virtual museum where visitors explore projects as interactive exhibits. Created custom 3D environment with Three.js, smooth camera animations with GSAP, animated Lottie character guide, and custom Blender models. Optimized for 60fps performance on both desktop and mobile devices.",
                technologies: "3D Design, Three.js, GSAP, Lottie, Blender",
                year: "2025",
                category: "Interactive Web Design",
                logo: "websitelogo.png",
                model3D: "donut.glb"
            }
        ];

        projectData.forEach((data, index) => {
            let geometry;
            
            switch (data.shape) {
                case 'box':
                    geometry = new THREE.BoxGeometry(...data.size);
                    break;
                case 'sphere':
                    geometry = new THREE.SphereGeometry(data.size);
                    break;
                case 'cone':
                    geometry = new THREE.ConeGeometry(...data.size);
                    break;
                case 'torus':
                    geometry = new THREE.TorusGeometry(...data.size);
                    break;
                case 'dodecahedron':
                    geometry = new THREE.DodecahedronGeometry(data.size);
                    break;
            }
            
            const material = new THREE.MeshLambertMaterial({
                color: data.color,
                transparent: true,
                opacity: 0.9
            });

            const mesh = new THREE.Mesh(geometry, material);
            mesh.position.copy(this.pedestals[index].position);
            mesh.position.y += 1.2;
            mesh.castShadow = true;

            // Apply responsive scaling to mesh
            const scaleFactor = this.app.deviceDetector.getResponsiveScaleFactor();
            mesh.scale.set(scaleFactor, scaleFactor, scaleFactor);

            const glowGeometry = geometry.clone();
            const glowMaterial = new THREE.MeshBasicMaterial({
                color: data.color,
                transparent: true,
                opacity: 0.2,
                side: THREE.BackSide
            });

            const glow = new THREE.Mesh(glowGeometry, glowMaterial);
            glow.scale.setScalar(1.1 * scaleFactor);
            glow.position.copy(mesh.position);
            
            // Create museum plaque for each artwork
            const plaque = this.createMuseumPlaque(data, index);
            
            // Show all artworks initially
            mesh.visible = true;
            glow.visible = true;
            plaque.visible = true;
            
            this.projectModels.push({ mesh, glow, plaque, data });
            this.scene.add(mesh);
            this.scene.add(glow);
            this.scene.add(plaque);
            
            this.animateProject(mesh, index);
        });
    }

    createMuseumPlaque(data, index) {
        const plaqueGroup = new THREE.Group();
        
        // Create smaller plaque base for pillar
        const plaqueGeometry = new THREE.BoxGeometry(1.5, 0.8, 0.1);
        const plaqueMaterial = new THREE.MeshLambertMaterial({ color: 0xf8f8f8 });
        const plaqueBase = new THREE.Mesh(plaqueGeometry, plaqueMaterial);
        
        // Create dark border
        const borderGeometry = new THREE.BoxGeometry(1.6, 0.9, 0.08);
        const borderMaterial = new THREE.MeshLambertMaterial({ color: 0x333333 });
        const border = new THREE.Mesh(borderGeometry, borderMaterial);
        border.position.z = -0.01;
        
        plaqueGroup.add(border);
        plaqueGroup.add(plaqueBase);
        
        // Add logo if available
        if (data.logo) {
            const textureLoader = new THREE.TextureLoader();
            textureLoader.load(
                data.logo,
                (texture) => {
                    // Set proper color space for accurate color reproduction
                    texture.colorSpace = THREE.SRGBColorSpace;

                    // Add anisotropic filtering for sharper textures at angles
                    texture.anisotropy = this.app.renderer.capabilities.getMaxAnisotropy();

                    // Create logo plane with larger size
                    const logoSize = 0.65; // Increased from 0.4 to 0.65
                    const logoGeometry = new THREE.PlaneGeometry(logoSize, logoSize);
                    
                    // Special handling for economy-v1-picture.png to blend white background with plaque
                    const isEconomyLogo = data.logo.includes('economy-v1-picture');
                    
                    const logoMaterial = new THREE.MeshBasicMaterial({
                        map: texture,
                        transparent: !isEconomyLogo, // Don't use transparency for economy logo
                        alphaTest: isEconomyLogo ? 0 : 0.1, // No alpha test for economy logo
                        side: THREE.DoubleSide,
                        depthWrite: false,
                        color: isEconomyLogo ? 0xf8f8f8 : 0xffffff, // Match plaque color for economy logo
                        toneMapped: false // Keep false to preserve vibrant colors
                    });
                    const logoMesh = new THREE.Mesh(logoGeometry, logoMaterial);
                    
                    // Position logo on the plaque surface
                    logoMesh.position.z = 0.06; // Slightly in front of plaque
                    logoMesh.renderOrder = 1; // Render after plaque
                    plaqueGroup.add(logoMesh);
                },
                undefined,
                (error) => {
                    console.error(`Error loading logo ${data.logo}:`, error);
                }
            );
        }
        
        // Position small plaque on the pillar part of pedestal
        plaqueGroup.position.copy(this.pedestals[index].position);
        plaqueGroup.position.z += 1.15; // Slightly inset into front face of pillar
        plaqueGroup.position.y += -0.3; // On the cylindrical pillar section
        plaqueGroup.rotation.y = 0; // Keep horizontal for readability
        
        return plaqueGroup;
    }

    animateProject(mesh, index) {
        const rotationSpeed = 0.5 + Math.random() * 0.5;
        const floatOffset = index * 0.5;
        
        gsap.to(mesh.rotation, {
            y: Math.PI * 2,
            duration: 4 / rotationSpeed,
            ease: "none",
            repeat: -1
        });
        
        gsap.to(mesh.position, {
            y: mesh.position.y + 0.3,
            duration: 2 + floatOffset,
            ease: "power2.inOut",
            yoyo: true,
            repeat: -1
        });
    }

    setupLighting() {
        // Warm golden ambient lighting for vintage museum atmosphere (2700K warmth)
        this.ambientLight = new THREE.AmbientLight(0xFFE4B5, 0.4);
        this.scene.add(this.ambientLight);

        // Warm directional light simulating vintage gallery lighting
        this.directionalLight = new THREE.DirectionalLight(0xFFE5CC, 0.6);
        this.directionalLight.position.set(0, 10, 5);
        this.directionalLight.castShadow = false;
        this.scene.add(this.directionalLight);

        // Warm vintage spotlights for each pedestal with golden glow
        for (let i = 0; i < this.totalProjects; i++) {
            const spotlight = new THREE.SpotLight(0xFFD9A0, 1.2, 20, Math.PI / 6, 0.5);
            spotlight.position.set(
                this.pedestals[i].position.x,
                6,
                this.pedestals[i].position.z + 2
            );
            spotlight.target.position.copy(this.pedestals[i].position);
            spotlight.target.position.y += 1;

            spotlight.castShadow = true;
            spotlight.shadow.mapSize.setScalar(512);

            // Make all spotlights visible initially
            spotlight.visible = true;

            this.scene.add(spotlight);
            this.scene.add(spotlight.target);

            this.pedestals[i].spotlight = spotlight;
        }
    }

    showPedestal(index) {
        // Hide all pedestals
        this.pedestals.forEach((pedestal, i) => {
            pedestal.visible = (i === index);
        });
        
        // Hide all project models and plaques
        this.projectModels.forEach((model, i) => {
            model.mesh.visible = (i === index);
            model.glow.visible = (i === index);
            model.plaque.visible = (i === index);
        });
        
        this.currentProject = index;
    }

    nextProject() {
        const newIndex = (this.currentProject + 1) % this.totalProjects;
        this.showPedestal(newIndex);
        return newIndex;
    }

    previousProject() {
        const newIndex = (this.currentProject - 1 + this.totalProjects) % this.totalProjects;
        this.showPedestal(newIndex);
        return newIndex;
    }

    showProject(index) {
        if (index < 0 || index >= this.totalProjects || index === this.currentProject) {
            return;
        }
        
        this.hideCurrentProject();
        
        setTimeout(() => {
            this.currentProject = index;
            this.revealProject(index);
        }, 300);
    }

    smoothTransitionToPedestal(index) {
        if (index < 0 || index >= this.totalProjects || index === this.currentProject) {
            return;
        }

        // Store the transition state to prevent multiple concurrent transitions
        if (this.isTransitioning) return;
        this.isTransitioning = true;

        // Fade out current project
        const current = this.currentProject;
        const timeline = gsap.timeline({
            onComplete: () => {
                this.isTransitioning = false;
            }
        });

        // Fade out current elements
        timeline.to([
            this.projectModels[current].mesh.material,
            this.projectModels[current].glow.material
        ], {
            opacity: 0,
            duration: 0.4,
            ease: "power2.out"
        })
        .to(this.pedestals[current].spotlight, {
            intensity: 0,
            duration: 0.4,
            ease: "power2.out"
        }, "-=0.4")
        
        // Switch visibility and fade in new elements
        .call(() => {
            // Hide current, show new
            this.showPedestal(index);
        })
        .to([
            this.projectModels[index].mesh.material,
            this.projectModels[index].glow.material
        ], {
            opacity: (i) => i === 0 ? 0.9 : 0.2, // Different opacity for mesh vs glow
            duration: 0.6,
            ease: "power2.out"
        })
        .to(this.pedestals[index].spotlight, {
            intensity: 1.5,
            duration: 0.6,
            ease: "power2.out"
        }, "-=0.6");

        this.currentProject = index;
    }

    hideCurrentProject() {
        const current = this.currentProject;
        
        gsap.to(this.pedestals[current].scale, {
            x: 0,
            y: 0,
            z: 0,
            duration: 0.5,
            ease: "power2.inOut",
            onComplete: () => {
                this.pedestals[current].visible = false;
                this.projectModels[current].mesh.visible = false;
                this.projectModels[current].glow.visible = false;
                this.projectModels[current].plaque.visible = false;
                this.pedestals[current].spotlight.visible = false;
            }
        });
        
        gsap.to(this.projectModels[current].mesh.scale, {
            x: 0,
            y: 0,
            z: 0,
            duration: 0.5,
            ease: "power2.inOut"
        });
    }

    revealProject(index) {
        this.pedestals[index].visible = true;
        this.projectModels[index].mesh.visible = true;
        this.projectModels[index].glow.visible = true;
        this.projectModels[index].plaque.visible = true;
        this.pedestals[index].spotlight.visible = true;
        
        this.pedestals[index].scale.setScalar(0);
        this.projectModels[index].mesh.scale.setScalar(0);
        
        gsap.to(this.pedestals[index].scale, {
            x: 1,
            y: 1,
            z: 1,
            duration: 0.8,
            ease: "back.out(1.7)",
            delay: 0.2
        });
        
        gsap.to(this.projectModels[index].mesh.scale, {
            x: 1,
            y: 1,
            z: 1,
            duration: 0.8,
            ease: "back.out(1.7)",
            delay: 0.4
        });
        
        const spotlight = this.pedestals[index].spotlight;
        spotlight.intensity = 0;
        gsap.to(spotlight, {
            intensity: 1,
            duration: 1,
            ease: "power2.out",
            delay: 0.3
        });
    }

    nextProject() {
        const nextIndex = (this.currentProject + 1) % this.totalProjects;
        this.showProject(nextIndex);
    }

    previousProject() {
        const prevIndex = (this.currentProject - 1 + this.totalProjects) % this.totalProjects;
        this.showProject(prevIndex);
    }

    setupInteractions() {
        // Add click listener for pedestal zoom
        this.app.canvas.addEventListener('click', this.onCanvasClick.bind(this));

        // Add mousemove listener for cursor change on booth hover
        this.app.canvas.addEventListener('mousemove', this.onMouseMove.bind(this));

        // Add touch gesture support for mobile navigation
        this.setupTouchGestures();
    }

    setupTouchGestures() {
        let touchStartX = 0;
        let touchStartY = 0;
        let touchStartTime = 0;

        this.app.canvas.addEventListener('touchstart', (e) => {
            if (!this.app.isInGallery() || !this.isZoomedIn) return;

            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
            touchStartTime = Date.now();
        }, { passive: true });

        this.app.canvas.addEventListener('touchend', (e) => {
            if (!this.app.isInGallery() || !this.isZoomedIn) return;

            const touchEndX = e.changedTouches[0].clientX;
            const touchEndY = e.changedTouches[0].clientY;
            const touchEndTime = Date.now();

            const deltaX = touchEndX - touchStartX;
            const deltaY = touchEndY - touchStartY;
            const deltaTime = touchEndTime - touchStartTime;

            // Detect horizontal swipe (minimum 30px, within 500ms) - easier threshold for mobile
            if (Math.abs(deltaX) > 30 && Math.abs(deltaX) > Math.abs(deltaY) && deltaTime < 500) {
                const currentIndex = this.zoomedPedestal !== null ? this.zoomedPedestal : 0;

                if (deltaX > 0 && currentIndex > 0) {
                    // Swipe right - go to previous pedestal
                    this.slideTowardsPedestal(currentIndex - 1);
                    this.app.updateProjectInfo(currentIndex - 1);

                    // Update progress dots
                    document.querySelectorAll('.progress-dot').forEach((dot, i) => {
                        dot.classList.toggle('active', i === currentIndex - 1);
                    });
                } else if (deltaX < 0 && currentIndex < this.totalProjects - 1) {
                    // Swipe left - go to next pedestal
                    this.slideTowardsPedestal(currentIndex + 1);
                    this.app.updateProjectInfo(currentIndex + 1);

                    // Update progress dots
                    document.querySelectorAll('.progress-dot').forEach((dot, i) => {
                        dot.classList.toggle('active', i === currentIndex + 1);
                    });
                }
            }
        }, { passive: true });
    }

    onMouseMove(event) {
        // Only handle mouse moves when we're in gallery mode
        if (!this.app.isInGallery()) return;

        // Don't check hover when zoomed in
        if (this.isZoomedIn) {
            if (this.isHoveringBooth) {
                this.app.canvas.style.cursor = 'default';
                this.isHoveringBooth = false;
            }
            return;
        }

        this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.app.camera);

        let isHoveringInteractive = false;

        // Check if hovering over booth clickbox
        if (this.boothClickbox && this.boothClickbox.visible) {
            const intersects = this.raycaster.intersectObject(this.boothClickbox, false);

            if (intersects.length > 0) {
                if (!this.isHoveringBooth) {
                    this.app.canvas.style.cursor = 'pointer';
                    this.isHoveringBooth = true;
                    console.log('Hovering over booth/character area');
                }
                isHoveringInteractive = true;
            } else {
                if (this.isHoveringBooth) {
                    this.isHoveringBooth = false;
                    console.log('Left booth/character area');
                }
            }
        } else {
            // Reset hover state if clickbox is not available
            if (this.isHoveringBooth) {
                this.isHoveringBooth = false;
            }
        }

        // Check if hovering over pedestals or artworks (only in overview mode)
        if (!isHoveringInteractive) {
            const visiblePedestals = this.pedestals.filter(pedestal => pedestal.visible);
            const visibleProjectItems = this.projectModels.filter(model => model.mesh.visible);

            // Combine pedestals and project items for hover detection
            const hoverableObjects = [
                ...visiblePedestals,
                ...visibleProjectItems.map(model => model.mesh),
                ...visibleProjectItems.map(model => model.glow)
            ];

            const intersects = this.raycaster.intersectObjects(hoverableObjects, true);

            if (intersects.length > 0) {
                this.app.canvas.style.cursor = 'pointer';
                isHoveringInteractive = true;

                // Find which project is being hovered
                let hoveredProjectIndex = -1;
                for (let i = 0; i < this.projectModels.length; i++) {
                    if (this.projectModels[i].mesh.visible && 
                        (intersects.some(intersect => intersect.object === this.projectModels[i].mesh) ||
                         intersects.some(intersect => intersect.object === this.projectModels[i].glow))) {
                        hoveredProjectIndex = i;
                        break;
                    }
                }

                // Apply hover glow effect to the hovered project
                this.projectModels.forEach((model, i) => {
                    if (model.mesh.visible) {
                        if (i === hoveredProjectIndex) {
                            // Brighten glow on hover
                            model.glow.material.opacity = 0.4;
                        } else {
                            // Normal glow
                            model.glow.material.opacity = 0.2;
                        }
                    }
                });
            } else {
                // Reset all glows to normal when not hovering
                this.projectModels.forEach((model) => {
                    if (model.mesh.visible) {
                        model.glow.material.opacity = 0.2;
                    }
                });
            }
        }

        // Reset cursor if not hovering over anything interactive
        if (!isHoveringInteractive) {
            this.app.canvas.style.cursor = 'default';
        }
    }

    onCanvasClick(event) {
        // Only handle clicks when we're in gallery mode
        if (!this.app.isInGallery()) return;

        // Ignore clicks during scroll navigation or zoom animation
        if (this.isScrolling || this.isZoomAnimating) return;

        // Prevent default behavior and stop propagation
        event.preventDefault();
        event.stopPropagation();

        this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.app.camera);

        if (this.isZoomedIn) {
            // If zoomed in, ignore canvas clicks (only back button should zoom out)
            return;
        } else {
            // Check if we clicked on the booth clickbox first
            if (this.boothClickbox && this.boothClickbox.visible) {
                const boothIntersects = this.raycaster.intersectObject(this.boothClickbox, false);
                if (boothIntersects.length > 0) {
                    console.log('Booth/character area clicked - opening chatbox');
                    this.showChatbox();
                    return;
                }
            }

            // Check if we clicked on any visible pedestal or floating project item
            const visiblePedestals = this.pedestals.filter(pedestal => pedestal.visible);
            const visibleProjectItems = this.projectModels.filter(model => model.mesh.visible);

            // Combine pedestals and project items for click detection
            const clickableObjects = [
                ...visiblePedestals,
                ...visibleProjectItems.map(model => model.mesh),
                ...visibleProjectItems.map(model => model.glow)
            ];

            const intersects = this.raycaster.intersectObjects(clickableObjects, true);

            if (intersects.length > 0) {
                let clickedPedestalIndex = -1;
                
                // First check if we clicked directly on a pedestal
                for (let i = 0; i < this.pedestals.length; i++) {
                    if (this.pedestals[i].visible && 
                        intersects.some(intersect => this.pedestals[i].children.includes(intersect.object))) {
                        clickedPedestalIndex = i;
                        break;
                    }
                }
                
                // If no pedestal was clicked, check if we clicked on a project item
                if (clickedPedestalIndex === -1) {
                    for (let i = 0; i < this.projectModels.length; i++) {
                        if (this.projectModels[i].mesh.visible && 
                            (intersects.some(intersect => intersect.object === this.projectModels[i].mesh) ||
                             intersects.some(intersect => intersect.object === this.projectModels[i].glow))) {
                            clickedPedestalIndex = i;
                            break;
                        }
                    }
                }

                if (clickedPedestalIndex >= 0) {
                    this.zoomInToPedestal(clickedPedestalIndex);
                    // Update the app's current pedestal index
                    if (this.app.updateCurrentPedestalIndex) {
                        this.app.updateCurrentPedestalIndex(clickedPedestalIndex);
                    }
                }
            }
        }
    }

    zoomInToPedestal(index) {
        // Prevent rapid clicking - require 300ms between zoom actions
        const now = Date.now();
        if (this.lastZoomClickTime && now - this.lastZoomClickTime < 300) {
            return; // Ignore rapid clicks
        }
        this.lastZoomClickTime = now;

        // Play project click sound
        this.app.soundManager.playProjectClick();

        // Kill any ongoing camera animations to prevent conflicts with zoom-out animation
        gsap.killTweensOf(this.app.camera.position);

        this.zoomedPedestal = index;
        this.isScrolling = true;
        this.isZoomAnimating = true;
        this.hasSwipedOnce = false; // Reset on each zoom in
        this.isCharacterAllowedToShow = true; // Reset flag when zooming in

        // Hide overview subtitles and show zoomed-in subtitles
        const overviewSubtitles = document.querySelectorAll('.gallery-overview-subtitle');
        const zoomedSubtitles = document.querySelectorAll('.gallery-zoomed-subtitle');
        overviewSubtitles.forEach(subtitle => subtitle.style.visibility = 'hidden');
        zoomedSubtitles.forEach(subtitle => subtitle.style.visibility = 'visible');

        // Show progress dots when zoomed in and update active dot
        const progressDots = document.querySelector('.progress-dots');
        if (progressDots) {
            progressDots.style.opacity = '1';
        }

        // Update progress dots to show current pedestal
        document.querySelectorAll('.progress-dot').forEach((dot, i) => {
            dot.classList.toggle('active', i === index);
        });

        // Show back button
        if (this.backButton) {
            this.backButton.style.opacity = '1';
            this.backButton.style.pointerEvents = 'auto';
        }

        // Tell camera controller we're taking control
        if (this.app.cameraController) {
            this.app.cameraController.isAnimating = true;
        }

        // Keep all pedestals visible but make non-selected ones translucent
        this.pedestals.forEach((pedestal, i) => {
            pedestal.visible = true; // Keep all visible
        });
        
        // Make all items visible but adjust opacity for focus
        this.projectModels.forEach((model, i) => {
            model.mesh.visible = true;
            model.plaque.visible = true; // Keep all plaques visible
            
            if (i !== index) {
                // Make non-selected items translucent
                gsap.to(model.mesh.material, {
                    opacity: 0.3,
                    duration: 0.5,
                    ease: "power2.out"
                });
                model.glow.visible = false;
            } else {
                // Keep selected item fully visible
                gsap.to(model.mesh.material, {
                    opacity: 1.0,
                    duration: 0.5,
                    ease: "power2.out"
                });
                model.glow.visible = true;
            }
        });

        // Create floating translucent writeup for the focused item
        this.createFloatingWriteup(index);

        // Use same positioning system as slideTowardsPedestal for consistency
        const targetX = (index - 2) * 6 - 1; // Slightly left of pedestal center for better plaque view
        const pedestal = this.pedestals[index];
        
        // Calculate smooth look-at target focused on artwork (plaque now at front)
        const lookAtTarget = {
            x: pedestal.position.x,      // Center on pedestal
            y: pedestal.position.y + 1.2, // Look at floating project item
            z: pedestal.position.z
        };

        // Instant camera positioning - position closer to pedestal (pedestals are at z=-5)
        // Mobile: higher camera position moved forward to reduce space below
        const cameraY = this.app.deviceDetector.isMobileSize() ? 2.2 : 1.8;
        const cameraZ = this.app.deviceDetector.isMobileSize() ? 0 : -2;
        this.app.camera.position.set(targetX, cameraY, cameraZ);
        this.app.camera.lookAt(lookAtTarget.x, lookAtTarget.y, lookAtTarget.z);

        // Set states immediately
        this.isZoomedIn = true;
        this.isZoomAnimating = false;
        this.isScrolling = false;
        
        // Release camera controller
        if (this.app.cameraController) {
            this.app.cameraController.isAnimating = false;
        }
    }

    slideTowardsPedestal(index) {
        // Smooth horizontal camera tracking for scroll navigation
        this.zoomedPedestal = index;
        this.isScrolling = true;
        this.isZoomAnimating = true;

        // Play navigation sound
        this.app.soundManager.playProjectClick();

        // Mark that user has swiped/scrolled, hide the zoomed subtitles
        if (!this.hasSwipedOnce) {
            this.hasSwipedOnce = true;
            const zoomedSubtitles = document.querySelectorAll('.gallery-zoomed-subtitle');
            zoomedSubtitles.forEach(subtitle => subtitle.style.visibility = 'hidden');
        }

        // Update progress dots to show current pedestal
        document.querySelectorAll('.progress-dot').forEach((dot, i) => {
            dot.classList.toggle('active', i === index);
        });
        
        // Tell camera controller we're taking control
        if (this.app.cameraController) {
            this.app.cameraController.isAnimating = true;
        }
        
        // Keep all pedestals visible
        this.pedestals.forEach((pedestal, i) => {
            pedestal.visible = true;
        });
        
        // Update opacity for highlighting during slide
        this.projectModels.forEach((model, i) => {
            model.mesh.visible = true;
            model.plaque.visible = true; // Keep all plaques visible
            
            if (i !== index) {
                // Make non-selected items translucent
                gsap.to(model.mesh.material, {
                    opacity: 0.3,
                    duration: 0.4,
                    ease: "power2.out"
                });
                model.glow.visible = false;
            } else {
                // Highlight the selected item
                gsap.to(model.mesh.material, {
                    opacity: 1.0,
                    duration: 0.4,
                    ease: "power2.out"
                });
                model.glow.visible = true;
            }
        });

        // Update floating writeup for the new selected item
        this.createFloatingWriteup(index);

        // Calculate target position for optimal pedestal/plaque framing
        const targetX = (index - 2) * 6 - 1; // Slightly left of pedestal center for better plaque view
        const pedestal = this.pedestals[index];

        // Calculate smooth look-at target focused on artwork (plaque now at front)
        const lookAtTarget = {
            x: pedestal.position.x,      // Center on pedestal
            y: pedestal.position.y + 1.2, // Look at floating project item
            z: pedestal.position.z
        };

        // Instant camera positioning - position closer to pedestal (pedestals are at z=-5)
        // Mobile: higher camera position moved forward to reduce space below
        const cameraY = this.app.deviceDetector.isMobileSize() ? 2.2 : 1.8;
        const cameraZ = this.app.deviceDetector.isMobileSize() ? 0 : -2;
        this.app.camera.position.set(targetX, cameraY, cameraZ);
        this.app.camera.lookAt(lookAtTarget.x, lookAtTarget.y, lookAtTarget.z);

        // Clear animation flags immediately since positioning is instant
        this.isZoomAnimating = false;
        this.isScrolling = false;

        // Release camera controller
        if (this.app.cameraController) {
            this.app.cameraController.isAnimating = false;
        }
    }

    highlightPedestal(index) {
        // Legacy method - now just calls slideTowardsPedestal for consistency
        this.slideTowardsPedestal(index);
    }

    createFloatingWriteup(index) {
        // Store reference to old writeup for cross-fade
        const oldWriteup = this.floatingWriteup;

        const projectData = this.projectModels[index].data;

        // Responsive sizing based on screen width
        const screenWidth = window.innerWidth;
        let boxWidth, boxPadding, bodyFontSize, outcomeFontSize, headerFontSize;

        if (screenWidth > 1024) {
            // Desktop
            boxWidth = '450px';
            boxPadding = '24px';
            bodyFontSize = '1em';
            outcomeFontSize = '0.95em';
            headerFontSize = '1em';
        } else if (screenWidth > 768) {
            // Tablet
            boxWidth = '350px';
            boxPadding = '22px';
            bodyFontSize = '0.95em';
            outcomeFontSize = '0.9em';
            headerFontSize = '0.95em';
        } else {
            // Mobile
            boxWidth = '300px';
            boxPadding = '20px';
            bodyFontSize = '0.9em';
            outcomeFontSize = '0.85em';
            headerFontSize = '0.95em';
        }

        // Create new floating writeup container
        this.floatingWriteup = document.createElement('div');
        this.floatingWriteup.id = 'floating-writeup';
        this.floatingWriteup.className = 'floating-writeup';
        this.floatingWriteup.style.cssText = `
            position: fixed;
            top: 20%;
            right: 5%;
            width: ${boxWidth};
            max-height: 60%;
            background: rgba(0, 0, 0, 0.75);
            color: white;
            padding: ${boxPadding};
            border-radius: 10px;
            font-family: 'Inter', sans-serif;
            z-index: 20;
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.2);
            overflow-y: auto;
        `;

        // Create content with enhanced UX-focused structure
        let content = `
            <h3 style="margin: 0 0 15px 0; color: #fff; font-size: 1.4em;">${projectData.title}</h3>
            <div style="margin-bottom: 15px; padding: 8px; background: rgba(255, 255, 255, 0.1); border-radius: 5px;">
                <strong style="color: #4ecdc4;">${projectData.category}</strong> • ${projectData.year}
            </div>
        `;

        // Add mockup image if it exists (moved to top)
        if (projectData.mockupImage) {
            content += `
                <div style="margin-bottom: 20px; border-radius: 8px; overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.2);">
                    <img src="/${projectData.mockupImage}"
                         alt="Project mockup"
                         style="width: 100%; height: auto; display: block;">
                </div>
            `;
        }

        // Add demo video if it exists (moved to top)
        if (projectData.demoVideo) {
            content += `
                <div style="margin-bottom: 20px; border-radius: 8px; overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.2);">
                    <video
                        src="/${projectData.demoVideo}"
                        autoplay
                        muted
                        loop
                        playsinline
                        style="width: 100%; height: auto; display: block;">
                    </video>
                </div>
            `;
        }

        // Add 3D model viewer if it exists (moved to top)
        if (projectData.model3D) {
            content += `
                <div id="model3d-viewer" style="margin-bottom: 20px; border-radius: 8px; overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.2); background: linear-gradient(180deg, #1a1a2e 0%, #0f0f1e 100%); position: relative;">
                    <canvas id="model3d-canvas" style="width: 100%; height: 180px; display: block; cursor: grab;"></canvas>
                    <div style="position: absolute; bottom: 10px; right: 10px; color: rgba(255,255,255,0.5); font-size: 0.75em; pointer-events: none;">
                        Drag to rotate • Scroll to zoom
                    </div>
                </div>
            `;
        }

        // Add problem statement if it exists
        if (projectData.problem) {
            content += `
                <div style="margin-bottom: 15px;">
                    <strong style="color: #ff6b6b; font-size: ${headerFontSize};">PROBLEM</strong>
                    <p style="margin: 5px 0 0 0; line-height: 1.5; color: #e0e0e0; font-size: ${bodyFontSize};">${projectData.problem}</p>
                </div>
            `;
        }

        // Add solution if it exists
        if (projectData.solution) {
            content += `
                <div style="margin-bottom: 15px;">
                    <strong style="color: #96ceb4; font-size: ${headerFontSize};">SOLUTION</strong>
                    <p style="margin: 5px 0 0 0; line-height: 1.5; color: #e0e0e0; font-size: ${bodyFontSize};">${projectData.solution}</p>
                </div>
            `;
        }

        // Add role if it exists
        if (projectData.role) {
            content += `
                <div style="margin-bottom: 15px;">
                    <strong style="color: #ffd93d; font-size: ${headerFontSize};">MY ROLE</strong>
                    <p style="margin: 5px 0 0 0; line-height: 1.5; color: #e0e0e0; font-size: ${bodyFontSize};">${projectData.role}</p>
                </div>
            `;
        }

        // Add outcomes if they exist
        if (projectData.outcomes) {
            content += `
                <div style="margin-bottom: 15px;">
                    <strong style="color: #a78bfa; font-size: ${headerFontSize};">KEY OUTCOMES</strong>
                    <p style="margin: 5px 0 0 0; line-height: 1.6; color: #e0e0e0; font-size: ${outcomeFontSize}; white-space: pre-line;">${projectData.outcomes}</p>
                </div>
            `;
        }

        // Fallback to description if new fields don't exist (for other projects)
        if (!projectData.problem && projectData.description) {
            content += `
                <p style="margin: 0 0 15px 0; line-height: 1.6; color: #e0e0e0; font-size: ${bodyFontSize};">${projectData.description}</p>
            `;
        }

        // Add technologies
        content += `
            <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid rgba(255, 255, 255, 0.2);">
                <strong style="color: #96ceb4; font-size: ${bodyFontSize};">TECHNOLOGIES:</strong><br>
                <span style="color: #ccc; font-size: ${bodyFontSize};">${projectData.technologies}</span>
            </div>
        `;

        this.floatingWriteup.innerHTML = content;

        // Start new writeup as invisible for cross-fade
        this.floatingWriteup.style.opacity = '0';
        document.body.appendChild(this.floatingWriteup);

        // Add wheel listener with stopPropagation for native scrolling
        this.floatingWriteup.addEventListener('wheel', (event) => {
            event.stopPropagation(); // Prevent pedestal navigation
        }, { passive: true });

        // Cross-fade: fade out old and fade in new simultaneously
        if (oldWriteup) {
            // Immediately remove from layout to prevent flash during position recalculation
            oldWriteup.style.display = 'none';

            gsap.to(oldWriteup, {
                opacity: 0,
                duration: 0.3,
                ease: "power2.inOut",
                onComplete: () => {
                    if (oldWriteup.parentNode) {
                        oldWriteup.parentNode.removeChild(oldWriteup);
                    }
                }
            });
        }

        // Cleanup old 3D model viewer immediately to prevent WebGL conflicts
        this.cleanup3DModelViewer();

        gsap.to(this.floatingWriteup, {
            opacity: 1,
            duration: 0.3,
            ease: "power2.inOut",
            onComplete: () => {
                // Setup 3D model viewer after fade-in completes
                // This ensures canvas is visible and has proper dimensions
                if (projectData.model3D) {
                    const canvas = document.getElementById('model3d-canvas');
                    if (canvas && canvas.clientWidth > 0) {
                        this.setup3DModelViewer(projectData.model3D);
                    } else {
                        console.warn('3D model canvas not ready or has invalid dimensions');
                    }
                }
            }
        });
    }
    
    removeFloatingWriteup() {
        if (this.floatingWriteup && this.floatingWriteup.parentNode) {
            this.floatingWriteup.parentNode.removeChild(this.floatingWriteup);
            this.floatingWriteup = null;
        }

        // Clean up 3D model viewer
        this.cleanup3DModelViewer();
    }

    setup3DModelViewer(modelPath) {
        const canvas = document.getElementById('model3d-canvas');
        if (!canvas) return;

        // Create separate scene, camera, and renderer for the 3D model viewer
        this.modelScene = new THREE.Scene();
        this.modelCamera = new THREE.PerspectiveCamera(50, canvas.clientWidth / canvas.clientHeight, 0.1, 100);
        this.modelCamera.position.set(0, 1, 4);

        this.modelRenderer = new THREE.WebGLRenderer({
            canvas: canvas,
            antialias: true,
            alpha: true
        });
        this.modelRenderer.setSize(canvas.clientWidth, canvas.clientHeight);
        this.modelRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

        // Add lighting for the model
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
        this.modelScene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 1.5);
        directionalLight.position.set(5, 5, 5);
        this.modelScene.add(directionalLight);

        const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight2.position.set(-5, 3, -5);
        this.modelScene.add(directionalLight2);

        // Check if model is already loaded in assets (avoid duplicate loading)
        let cachedAsset = null;
        if (modelPath === 'donut.glb' && this.app.assets.donut) {
            cachedAsset = this.app.assets.donut;
            console.log('Using cached donut model (no network request)');
        } else if (modelPath === 'booth.glb' && this.app.assets.booth) {
            cachedAsset = this.app.assets.booth;
            console.log('Using cached booth model (no network request)');
        }

        if (cachedAsset) {
            // Use cached model - clone it to avoid modifying the original
            this.model3DObject = cachedAsset.scene.clone();

            // Center and scale the model
            const box = new THREE.Box3().setFromObject(this.model3DObject);
            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());

            const maxDim = Math.max(size.x, size.y, size.z);
            const scale = 2 / maxDim;
            this.model3DObject.scale.setScalar(scale);

            this.model3DObject.position.sub(center.multiplyScalar(scale));

            this.modelScene.add(this.model3DObject);

            // Start animation loop
            this.animate3DModel();
        } else {
            // Load from network (for models not in cache)
            const loader = new GLTFLoader();
            loader.load(
                `/${modelPath}`,
                (gltf) => {
                    this.model3DObject = gltf.scene;

                    // Center and scale the model
                    const box = new THREE.Box3().setFromObject(this.model3DObject);
                    const center = box.getCenter(new THREE.Vector3());
                    const size = box.getSize(new THREE.Vector3());

                    const maxDim = Math.max(size.x, size.y, size.z);
                    const scale = 2 / maxDim;
                    this.model3DObject.scale.setScalar(scale);

                    this.model3DObject.position.sub(center.multiplyScalar(scale));

                    this.modelScene.add(this.model3DObject);

                    // Start animation loop
                    this.animate3DModel();
                },
                undefined,
                (error) => {
                    console.error('Error loading 3D model:', error);
                }
            );
        }

        // Add mouse controls
        this.setup3DControls(canvas);
    }

    setup3DControls(canvas) {
        let isDragging = false;
        let previousMousePosition = { x: 0, y: 0 };
        let modelRotation = { x: 0, y: 0 };
        let modelZoom = 4;

        canvas.addEventListener('mousedown', (event) => {
            isDragging = true;
            canvas.style.cursor = 'grabbing';
            previousMousePosition = { x: event.clientX, y: event.clientY };
        });

        canvas.addEventListener('mousemove', (event) => {
            if (!isDragging || !this.model3DObject) return;

            const deltaX = event.clientX - previousMousePosition.x;
            const deltaY = event.clientY - previousMousePosition.y;

            modelRotation.y += deltaX * 0.01;
            modelRotation.x += deltaY * 0.01;

            this.model3DObject.rotation.y = modelRotation.y;
            this.model3DObject.rotation.x = modelRotation.x;

            previousMousePosition = { x: event.clientX, y: event.clientY };
        });

        canvas.addEventListener('mouseup', () => {
            isDragging = false;
            canvas.style.cursor = 'grab';
        });

        canvas.addEventListener('mouseleave', () => {
            isDragging = false;
            canvas.style.cursor = 'grab';
        });

        // Zoom with mouse wheel
        canvas.addEventListener('wheel', (event) => {
            event.preventDefault();
            event.stopPropagation();

            modelZoom += event.deltaY * 0.01;
            modelZoom = Math.max(2, Math.min(10, modelZoom));
            this.modelCamera.position.z = modelZoom;
        }, { passive: false });

        // Touch controls for mobile
        let touchStartDistance = 0;
        canvas.addEventListener('touchstart', (event) => {
            if (event.touches.length === 1) {
                isDragging = true;
                previousMousePosition = { x: event.touches[0].clientX, y: event.touches[0].clientY };
            } else if (event.touches.length === 2) {
                const dx = event.touches[0].clientX - event.touches[1].clientX;
                const dy = event.touches[0].clientY - event.touches[1].clientY;
                touchStartDistance = Math.sqrt(dx * dx + dy * dy);
            }
        });

        canvas.addEventListener('touchmove', (event) => {
            event.preventDefault();

            if (event.touches.length === 1 && isDragging && this.model3DObject) {
                const deltaX = event.touches[0].clientX - previousMousePosition.x;
                const deltaY = event.touches[0].clientY - previousMousePosition.y;

                modelRotation.y += deltaX * 0.01;
                modelRotation.x += deltaY * 0.01;

                this.model3DObject.rotation.y = modelRotation.y;
                this.model3DObject.rotation.x = modelRotation.x;

                previousMousePosition = { x: event.touches[0].clientX, y: event.touches[0].clientY };
            } else if (event.touches.length === 2) {
                const dx = event.touches[0].clientX - event.touches[1].clientX;
                const dy = event.touches[0].clientY - event.touches[1].clientY;
                const distance = Math.sqrt(dx * dx + dy * dy);

                const delta = touchStartDistance - distance;
                modelZoom += delta * 0.01;
                modelZoom = Math.max(2, Math.min(10, modelZoom));
                this.modelCamera.position.z = modelZoom;

                touchStartDistance = distance;
            }
        }, { passive: false });

        canvas.addEventListener('touchend', () => {
            isDragging = false;
        });
    }

    animate3DModel() {
        if (!this.modelRenderer || !this.modelScene || !this.modelCamera) return;

        this.modelAnimationId = requestAnimationFrame(() => this.animate3DModel());

        // Clear the renderer before rendering to prevent ghosting/duplication on mobile
        this.modelRenderer.clear();
        this.modelRenderer.render(this.modelScene, this.modelCamera);
    }

    cleanup3DModelViewer() {
        // Stop animation loop
        if (this.modelAnimationId) {
            cancelAnimationFrame(this.modelAnimationId);
            this.modelAnimationId = null;
        }

        // Dispose of Three.js objects
        if (this.model3DObject) {
            this.modelScene.remove(this.model3DObject);
            this.model3DObject.traverse((child) => {
                if (child.geometry) child.geometry.dispose();
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(mat => mat.dispose());
                    } else {
                        child.material.dispose();
                    }
                }
            });
            this.model3DObject = null;
        }

        if (this.modelRenderer) {
            this.modelRenderer.dispose();
            this.modelRenderer = null;
        }

        this.modelScene = null;
        this.modelCamera = null;
    }

    showChatbox() {
        // Remove existing chatbox if any
        this.removeChatbox();

        // Create chatbox overlay backdrop
        this.chatboxBackdrop = document.createElement('div');
        this.chatboxBackdrop.id = 'chatbox-backdrop';
        this.chatboxBackdrop.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.7);
            z-index: 100;
            display: flex;
            justify-content: center;
            align-items: center;
            backdrop-filter: blur(5px);
            opacity: 0;
            transition: opacity 0.3s ease;
        `;

        // Create chatbox container with Stardew Valley style
        this.chatbox = document.createElement('div');
        this.chatbox.id = 'gallery-chatbox';
        this.chatbox.style.cssText = `
            position: relative;
            width: 90%;
            max-width: 550px;
            max-height: 80vh;
            background: #E8DCC8;
            color: #3E2723;
            padding: 2rem 2.5rem;
            border-radius: 0;
            font-family: 'Inter', sans-serif;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
            border: 4px solid #3E2723;
            transform: scale(0.8);
            transition: transform 0.3s ease;
            overflow-y: auto;
            user-select: text;
            -webkit-user-select: text;
        `;

        // Create simple close button
        const closeButton = document.createElement('button');
        closeButton.innerHTML = '✕';
        closeButton.style.cssText = `
            position: absolute;
            top: 0.75rem;
            right: 0.75rem;
            background: transparent;
            border: none;
            color: #3E2723;
            font-size: 1.8rem;
            font-weight: bold;
            width: 30px;
            height: 30px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            line-height: 1;
            padding: 0;
        `;
        closeButton.addEventListener('mouseenter', () => {
            closeButton.style.color = '#8B4513';
        });
        closeButton.addEventListener('mouseleave', () => {
            closeButton.style.color = '#3E2723';
        });
        closeButton.addEventListener('click', () => this.removeChatbox());

        // Create content with simple Stardew Valley style
        this.chatbox.innerHTML = `
            <h2 style="margin: 0 0 1.5rem 0; color: #3E2723; font-size: 1.6rem; font-weight: 600; border-bottom: 2px solid #3E2723; padding-bottom: 0.75rem;">
                Welcome to Choon Pin's Gallery of Projects
            </h2>
            <div style="margin-bottom: 2rem; line-height: 1.8; color: #543B2F;">
                <p style="margin-bottom: 1rem;">
                    Thank you for visiting this curated collection of my professional work. Each piece on display represents a project where design and technical execution come together.
                </p>
                <p style="margin-bottom: 1.5rem;">
                    Feel free to explore the gallery by clicking on any artwork to view details, or use your scroll wheel to navigate between exhibits.
                </p>
            </div>
            <a id="resume-link" href="Wong Choon Pin_Resume_2026.pdf" target="_blank" style="
                display: inline-block;
                padding: 0.75rem 1.75rem;
                background: #8B6914;
                color: #F5F5DC;
                text-decoration: none;
                border: 3px solid #3E2723;
                font-weight: 600;
                font-size: 1rem;
                transition: all 0.2s ease;
            ">
                Come take a brochure!
            </a>
        `;

        // Add close button to chatbox
        this.chatbox.appendChild(closeButton);

        // Add simple hover effect to resume link
        const resumeLink = this.chatbox.querySelector('#resume-link');
        resumeLink.addEventListener('mouseenter', () => {
            resumeLink.style.background = '#A68A2E';
        });
        resumeLink.addEventListener('mouseleave', () => {
            resumeLink.style.background = '#8B6914';
        });

        // Append chatbox to backdrop
        this.chatboxBackdrop.appendChild(this.chatbox);

        // Append to document body
        document.body.appendChild(this.chatboxBackdrop);

        // Add wheel listener to chatbox with stopPropagation for native scrolling
        this.chatbox.addEventListener('wheel', (event) => {
            event.stopPropagation(); // Prevent pedestal navigation
        }, { passive: true });

        // Trigger animations
        setTimeout(() => {
            this.chatboxBackdrop.style.opacity = '1';
            this.chatbox.style.transform = 'scale(1)';
        }, 10);

        // Close on backdrop click
        this.chatboxBackdrop.addEventListener('click', (e) => {
            if (e.target === this.chatboxBackdrop) {
                this.removeChatbox();
            }
        });
    }

    removeChatbox() {
        if (this.chatboxBackdrop && this.chatboxBackdrop.parentNode) {
            this.chatboxBackdrop.style.opacity = '0';
            if (this.chatbox) {
                this.chatbox.style.transform = 'scale(0.8)';
            }
            setTimeout(() => {
                if (this.chatboxBackdrop && this.chatboxBackdrop.parentNode) {
                    this.chatboxBackdrop.parentNode.removeChild(this.chatboxBackdrop);
                }
                this.chatboxBackdrop = null;
                this.chatbox = null;
            }, 300);
        }
    }

    zoomOut() {
        if (!this.isZoomedIn) return;

        this.isZoomedIn = false;
        this.zoomedPedestal = null;
        this.isZoomAnimating = true;
        this.isScrolling = false; // Explicitly clear scrolling flag
        this.isCharacterAllowedToShow = false; // Block character from showing during zoom out

        // Show overview subtitles and hide zoomed-in subtitles
        const overviewSubtitles = document.querySelectorAll('.gallery-overview-subtitle');
        const zoomedSubtitles = document.querySelectorAll('.gallery-zoomed-subtitle');
        overviewSubtitles.forEach(subtitle => subtitle.style.visibility = 'visible');
        zoomedSubtitles.forEach(subtitle => subtitle.style.visibility = 'hidden');

        // Hide progress dots when in overview mode
        const progressDots = document.querySelector('.progress-dots');
        if (progressDots) {
            progressDots.style.opacity = '0';
        }

        // Hide back button
        if (this.backButton) {
            this.backButton.style.opacity = '0';
            this.backButton.style.pointerEvents = 'none';
        }

        // Remove floating writeup
        this.removeFloatingWriteup();

        // Tell camera controller we're taking control during zoom out
        if (this.app.cameraController) {
            this.app.cameraController.isAnimating = true;
        }

        // Restore all pedestals and artworks to full visibility
        this.pedestals.forEach((pedestal) => {
            pedestal.visible = true;
        });
        
        this.projectModels.forEach((model) => {
            model.mesh.visible = true;
            model.glow.visible = true;
            model.plaque.visible = true;
            model.mesh.material.opacity = 0.9;
            model.glow.material.opacity = 0.2;
        });

        // Smooth zoom out back to overview position
        gsap.to(this.app.camera.position, {
            x: 0,
            y: 5,
            z: 12,
            duration: 1.5,
            ease: "power1.inOut",
            onUpdate: () => {
                // Look at gallery center during zoom out
                this.app.camera.lookAt(0, 1, -5);
            },
            onComplete: () => {
                // Force immediate character update to make it appear instantly
                this.updateCharacterPosition();

                // Release camera controller back to normal operation
                if (this.app.cameraController) {
                    this.app.cameraController.isAnimating = false;
                }
            }
        });

        // Clear animation flags immediately to allow instant clicks after zoom out
        // The camera animation runs in background but shouldn't block user interaction
        setTimeout(() => {
            this.isZoomAnimating = false;
            this.isScrolling = false;
            if (this.app.cameraController) {
                this.app.cameraController.isAnimating = false;
            }
        }, 100); // Clear flags after 100ms to allow immediate re-zoom

        // Allow character to show after 750ms (halfway through 1.5s animation)
        setTimeout(() => {
            this.isCharacterAllowedToShow = true;
            this.updateCharacterPosition();
        }, 750);

        console.log('Zooming out to overview');
    }

    getProjectData(index) {
        return this.projectModels[index]?.data;
    }

    update() {
        // Only update character position when in gallery mode
        if (this.app.isInGallery()) {
            this.updateCharacterPosition();
        }
    }

    updateCharacterPosition() {
        if (!this.characterMarker || !this.characterContainer || !this.app.camera) {
            return;
        }

        // Hide character when zoomed in - return BEFORE any position calculations
        if (this.isZoomedIn) {
            this.characterContainer.style.opacity = '0';
            this.characterContainer.style.pointerEvents = 'none';
            return;
        }

        // Hide character until explicitly allowed (handles 750ms delay during zoom out)
        if (!this.isCharacterAllowedToShow) {
            this.characterContainer.style.opacity = '0';
            this.characterContainer.style.pointerEvents = 'none';
            return;
        }

        // Only calculate and update position if character should be visible
        // Create a vector for the marker's world position
        const markerPosition = new THREE.Vector3();
        this.characterMarker.getWorldPosition(markerPosition);

        // Project 3D position to screen coordinates
        const screenPosition = markerPosition.clone();
        screenPosition.project(this.app.camera);

        // Convert normalized device coordinates to pixel coordinates
        const x = (screenPosition.x * 0.5 + 0.5) * window.innerWidth;
        const y = (-screenPosition.y * 0.5 + 0.5) * window.innerHeight;

        // Make character visible
        this.characterContainer.style.opacity = '1';
        this.characterContainer.style.pointerEvents = 'auto';

        // On mobile: Force character to bottom-left (mirrors music button position)
        // On desktop: Use dynamic 3D projection positioning
        if (this.app.deviceDetector.isMobileSize() || this.app.deviceDetector.isMobile) {
            this.characterContainer.style.left = '1.2rem';
            this.characterContainer.style.top = 'auto';
            this.characterContainer.style.bottom = '1.2rem';
            this.characterContainer.style.transform = 'scaleX(-1) rotate(10deg)';
        } else {
            // Original dynamic positioning for desktop
            this.characterContainer.style.left = `${x}px`;
            this.characterContainer.style.top = `${y}px`;
            this.characterContainer.style.bottom = 'auto';
            this.characterContainer.style.transform = 'translate(-50%, -50%) scaleX(-1) rotate(10deg)';
        }
    }

    onResize() {
        // Update responsive scaling for pedestals and project displays
        const scaleFactor = this.app.deviceDetector.getResponsiveScaleFactor();

        // Update pedestal scaling
        this.pedestals.forEach(pedestal => {
            pedestal.scale.set(scaleFactor, scaleFactor, scaleFactor);
        });

        // Update project display scaling
        this.projectModels.forEach(model => {
            model.mesh.scale.set(scaleFactor, scaleFactor, scaleFactor);
            model.glow.scale.setScalar(1.1 * scaleFactor);
        });

        // Update booth visibility based on device size (handles orientation changes)
        if (this.booth) {
            const isMobileOrTablet = this.app.deviceDetector.isMobileSize() ||
                                    this.app.deviceDetector.isTabletSize() ||
                                    this.app.deviceDetector.isMobile;
            this.booth.visible = !isMobileOrTablet;
        }

        // Update back button position and style based on device size
        this.updateBackButtonStyle();
    }

    dispose() {
        this.scene.traverse((object) => {
            if (object.geometry) object.geometry.dispose();
            if (object.material) {
                if (Array.isArray(object.material)) {
                    object.material.forEach(material => material.dispose());
                } else {
                    object.material.dispose();
                }
            }
        });

        // Clean up back button
        if (this.backButton && this.backButton.parentNode) {
            this.backButton.parentNode.removeChild(this.backButton);
            this.backButton = null;
        }
    }

    hide() {
        // Hide all gallery scene elements to prevent interaction in other scenes
        if (this.booth) {
            this.booth.visible = false;
        }
        if (this.boothClickbox) {
            this.boothClickbox.visible = false;
            // Completely disable raycasting by removing raycast function
            this.boothClickbox.raycast = function() {};
        }
        this.pedestals.forEach(p => p.visible = false);
        this.projectModels.forEach(m => {
            m.mesh.visible = false;
            m.glow.visible = false;
            m.plaque.visible = false;
        });

        // Hide character when not in gallery scene
        if (this.characterContainer) {
            this.characterContainer.style.opacity = '0';
            this.characterContainer.style.pointerEvents = 'none';
        }
    }

    show() {
        // Show all gallery scene elements when entering gallery
        if (this.booth) {
            this.booth.visible = true;
        }
        if (this.boothClickbox) {
            this.boothClickbox.visible = true;
            // Restore raycasting
            this.boothClickbox.raycast = THREE.Mesh.prototype.raycast;
        }
        this.pedestals.forEach(p => p.visible = true);
        this.projectModels.forEach(m => {
            m.mesh.visible = true;
            m.glow.visible = true;
            m.plaque.visible = true;
        });

        // Enable character container for interaction
        if (this.characterContainer) {
            // Force an immediate update of character position
            this.updateCharacterPosition();
        }

        // Hide scroll indicator and progress dots initially (only show when zoomed in)
        const scrollIndicator = document.querySelector('.gallery-scroll-indicator');
        if (scrollIndicator) {
            scrollIndicator.style.display = 'none';
        }

        const progressDots = document.querySelector('.progress-dots');
        if (progressDots) {
            progressDots.style.opacity = '0';
        }

        // Show chatbox on first entry to gallery scene
        if (!this.hasShownInitialChatbox) {
            this.hasShownInitialChatbox = true;
            this.showChatbox();
        }
    }
}